"use client"

export const dynamic = 'force-dynamic';

import { useEffect, useState } from "react";
import TaskDetailModal from '@/components/tasks/TaskDetailModal';
import { showError } from '@/lib/toast';
import { useGatewayEvents } from '@/lib/useGatewayEvents';

interface TerminalOutput {
  id: string;
  type: string;
  content: string;
  createdAt: string;
}

interface TerminalCommand {
  id: string;
  command: string;
  createdAt: string;
}

interface TerminalSession {
  id: string;
  status: string;
  cwd: string | null;
  createdAt: string;
  closedAt: string | null;
  commands: TerminalCommand[];
  outputs: TerminalOutput[];
}

export default function TasksPage() {
  const [tasks, setTasks] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [filterPlanId, setFilterPlanId] = useState('');

  const [error, setError] = useState<string | null>(null);

  // detail modal state - now using modal instead of inline
  const [selectedTask, setSelectedTask] = useState<any | null>(null);

  async function load() {
    try {
      setError(null);
      let url = "/api/tasks";
      if (filterPlanId) url += `?jobId=${encodeURIComponent(filterPlanId)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setTasks(data.tasks || []);
      const c: Record<string, number> = {};
      (data.tasks || []).forEach((t: any) => {
        c[t.status] = (c[t.status] || 0) + 1;
      });
      setCounts(c);
    } catch (e: any) {
      console.error('failed to load tasks', e);
      const errorMsg = e.message || String(e);
      setError(errorMsg);
      showError(`Không thể tải danh sách tasks: ${errorMsg}`);
    }
  }

  useEffect(() => {
    load();
  }, []);

  useGatewayEvents(
    ['task.created', 'task.started', 'task.progress', 'task.completed', 'task.failed'],
    () => {
      void load();
    },
  );

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("vi-VN");
  };

  return (
    <div style={{ padding: 20 }} className="dashboard-container">
      <h1>Tasks</h1>
      {error && <p style={{ color: 'red' }}>Error loading tasks: {error}</p>}
      <p className="mb-4">
        pending: {counts.pending || 0} &nbsp; running: {counts.running || 0} &nbsp; completed: {counts.completed || 0} &nbsp; retry: {counts.retry || 0} &nbsp; failed: {counts.failed || 0}
      </p>
      <div className="mb-4 flex space-x-2">
        <input
          className="border px-2 py-1 rounded"
          placeholder="filter by plan id"
          value={filterPlanId}
          onChange={(e) => setFilterPlanId(e.target.value)}
        />
        <button className="bg-blue-500 text-white px-3 py-1 rounded" onClick={load}>Go</button>
      </div>
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan No</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Worker</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retries</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {tasks.slice(page*pageSize, (page+1)*pageSize).map((t) => (
            <tr key={t.id} className="hover:bg-gray-100 cursor-pointer" onClick={() => setSelectedTask(t)}>
              <td className="px-6 py-4 whitespace-nowrap">{t.jobNo || '-'}</td>
              <td className="px-6 py-4 max-w-xs" title={t.jobId}>
                <div className="truncate">{t.jobId || '-'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">{t.worker}</td>
              <td className="px-6 py-4 whitespace-nowrap">{t.status}</td>
              <td className="px-6 py-4 whitespace-nowrap">{t.retryCount}</td>
              <td className="px-6 py-4 whitespace-nowrap">{new Date(t.createdAt).toLocaleTimeString()}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <button 
                  className="text-blue-600 hover:underline" 
                  onClick={(e) => { e.stopPropagation(); setSelectedTask(t); }}
                >
                  View
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      <div style={{ marginTop: 8 }}>
        <button onClick={() => setPage((p) => Math.max(p-1,0))} disabled={page===0}>Prev</button>
        <span style={{ margin: '0 8px' }}>page {page+1}</span>
        <button onClick={() => setPage((p) => p+1)} disabled={(page+1)*pageSize >= tasks.length}>Next</button>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
        />
      )}
    </div>
  );
}
